import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, MapPin, LayoutGrid } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavLocations, useNavLocation } from "../components/nav/useNavPrefs";

export function useMobileLocation() {
  const { user } = useAuth();
  const locations = useNavLocations(user);
  const [location, setLocation] = useNavLocation(locations);
  return { locations, location, setLocation };
}

export function LocationSelect({ allowAll = true }) {
  const { locations, location, setLocation } = useMobileLocation();
  if (!locations.length) return null;
  return (
    <label className="relative block" data-testid="m-location">
      <MapPin className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-tertiary pointer-events-none" />
      <select value={location} onChange={(e) => setLocation(e.target.value)} data-testid="m-location-select"
        className="w-full appearance-none bg-white border border-brand-border rounded-xl pl-9 pr-4 py-3 text-base text-brand-ink focus:outline-none focus:border-brand-ink">
        {allowAll ? <option value="">All locations</option> : <option value="" disabled>Pick a location</option>}
        {locations.map((l) => <option key={l} value={l}>{l}</option>)}
      </select>
    </label>
  );
}

export default function MobileShell({ title, back = "/m", children, right }) {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-brand-page text-brand-ink" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="m-shell">
      <header className="sticky top-0 z-20 bg-brand-page/90 backdrop-blur-md border-b border-brand-border">
        <div className="max-w-2xl mx-auto flex items-center gap-2 px-4 h-14">
          {back ? (
            <button onClick={() => nav(back)} className="-ml-2 p-2 rounded-full active:bg-brand-hover" aria-label="Back" data-testid="m-back"><ChevronLeft className="h-6 w-6" /></button>
          ) : <span className="font-serif text-xl">Bond</span>}
          <h1 className="flex-1 text-lg font-medium truncate" data-testid="m-title">{title}</h1>
          {right}
          <Link to="/home" className="inline-flex items-center gap-1 text-xs text-brand-muted border border-brand-border rounded-full px-3 py-1.5 active:bg-brand-hover" data-testid="m-full-hub"><LayoutGrid className="h-3.5 w-3.5" /> Full Hub</Link>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-4">{children}</main>
    </div>
  );
}

export const Card = ({ children, className = "", ...p }) => <div className={`bg-brand-surface border border-brand-border rounded-2xl ${className}`} {...p}>{children}</div>;
export const BigButton = ({ children, className = "", ...p }) => (
  <button className={`w-full min-h-[48px] rounded-xl px-4 text-base font-medium active:scale-[0.98] transition-transform disabled:opacity-50 ${className}`} {...p}>{children}</button>
);
export const Empty = ({ children, ...p }) => <p className="text-center text-sm text-brand-tertiary py-10" {...p}>{children}</p>;
export const Skeleton = () => <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-20 rounded-2xl bg-brand-hover animate-pulse" />)}</div>;
